import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  DestroyRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  inject,
  input,
  Input,
  output,
  Output,
  Signal,
  TemplateRef,
  viewChild,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { TemplateWithValueDirective } from '@coin/shared/core/util-directives';
import { DefaultControlValueAccessor } from '@coin/shared/core/util-helpers';
import { TranslateModule } from '@ngx-translate/core';
import { InputSize, MatFormFieldClass } from '../../input.types';
import { InputDebounceTime, V2TextInputComponent } from '../text-input/v2-text-input.component';

@Component({
  selector: 'coin-v2-dropdown',
  templateUrl: './v2-dropdown.component.html',
  styleUrls: ['./v2-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    V2TextInputComponent,
    MatSelectModule,
    MatInputModule,
    TranslateModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltip
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => V2DropdownComponent),
      multi: true
    }
  ]
})
export class V2DropdownComponent<T = unknown> extends DefaultControlValueAccessor<T> implements AfterViewInit {
  private destroy = inject(DestroyRef);

  @Input() label: string;
  @Input() placeholder: string;
  @Input() matFormFieldClass: MatFormFieldClass = 'customer-grey';
  @Input() multiple = false;
  @Input() loading: boolean;
  @Input() hiddenForm: boolean;
  @Input() size: InputSize = 'medium';
  @Input() searchDebounce: InputDebounceTime = 300;
  @Input() compareWith: (o1: T, o2: T) => boolean = (o1, o2) => o1 === o2;
  topTemplate = input<TemplateRef<unknown>>();
  bottomTemplate = input<TemplateRef<unknown>>();
  panelClass = input<string>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() search = new EventEmitter<string>();
  openedChange = output<boolean>();
  selectedValueTemplate = input<TemplateRef<T>>();
  options: Signal<readonly TemplateWithValueDirective<T>[]> = contentChildren(TemplateWithValueDirective);
  @ViewChild(V2TextInputComponent) searchInput: V2TextInputComponent;
  matSelect = viewChild(MatSelect);

  protected searchControl = new FormControl('');

  @HostBinding('attr.aria-hidden')
  get hasNoOptions() {
    return !this.options()?.length ? true : null;
  }

  @HostBinding('class')
  private get class(): string {
    return `size-${this.size} ${this.hiddenForm ? 'hidden-form' : ''}`;
  }

  panelHeightClass = computed(() => {
    return `mat-select-height${this.search.observed ? '-search' : ''}${this.bottomTemplate() ? '-bottom' : ''} ${this.panelClass()}`;
  });

  protected get selectTriggerType() {
    if (!this.value || (Array.isArray(this.value) && !this.value[0])) return null;

    if (Array.isArray(this.value) && this.value.length > 1) {
      return 'multiple-values';
    }
    if (
      this.selectedValueTemplate() instanceof TemplateRef &&
      (!Array.isArray(this.value) || this.value.length === 1)
    ) {
      return 'template';
    }

    return null; // default display
  }

  public ngAfterViewInit(): void {
    this.searchControl.valueChanges.pipe(takeUntilDestroyed(this.destroy)).subscribe(value => this.search.emit(value));
  }

  protected onOpenedChange(opened: boolean): void {
    if (opened) {
      this.focusSearchInputIfPresent();
      this.scrollPanelToTop();
    }
    this.openedChange.emit(opened);
  }

  private scrollPanelToTop(): void {
    const panel = document.querySelector('.mat-mdc-select-panel');
    if (panel) {
      panel.scrollTop = 0;
    }
  }

  public triggerDropdown() {
    this.matSelect()?.open();
  }

  private focusSearchInputIfPresent(): void {
    this.searchInput?.focus();
  }

  public resetSearch(): void {
    this.searchControl.reset('');
  }

  public openDropdown(): void {
    this.matSelect().open();
  }

  public closePanel() {
    this.matSelect().close();
  }
}
